import type { VerifyContainer, VerifyState } from "~/store/verifySlice";

import logger from "~/helpers/logger";

export interface LegacyQueuedContainerSubmit {
	containerId: string;
	fireVerification: boolean;
	id: string;
	label: string;
	orderIds: string[];
	queuedAt: string;
	status: "failed" | "pending" | "posted" | "posting";
	verificationPosted: boolean;
}

export interface LegacyPendingOrderVerification {
	orderIds?: string[];
	postedOrderIds?: string[];
}

export interface LegacyVerifyState extends Omit<VerifyState, "activeContainerId"> {
	activeContainerId?: string | null;
	activeLabel?: string | null;
	lastSyncTimestamp?: string | null;
	pendingVerifications?: Record<string, LegacyPendingOrderVerification>;
	submitQueue?: LegacyQueuedContainerSubmit[];
}

const isUnposted = (entry: LegacyQueuedContainerSubmit): boolean => entry.status !== "posted";

const reportStrandedVerifications = (unposted: LegacyQueuedContainerSubmit[]): void => {
	const stranded = unposted.filter((entry) => entry.fireVerification && !entry.verificationPosted);
	if (stranded.length === 0) return;
	logger.error("[verify] legacy queue entries carried unposted order verifications — re-verify from the dashboard", {
		strandedEntries: stranded.map(({ containerId, label, orderIds, queuedAt }) => ({
			containerId,
			label,
			orderIds,
			queuedAt
		}))
	});
};

const reportDroppedAutomaticVerifications = (
	pendingVerifications: Record<string, LegacyPendingOrderVerification> | undefined
): void => {
	const unpostedOrderIds = Object.values(pendingVerifications ?? {}).flatMap(({ orderIds = [], postedOrderIds = [] }) =>
		orderIds.filter((orderId) => !postedOrderIds.includes(orderId))
	);
	if (unpostedOrderIds.length === 0) return;
	logger.error("[verify] automatic order verifications were dropped unsent; verify these orders from the dashboard", {
		orderIds: [...new Set(unpostedOrderIds)]
	});
};

const clearSubmitStampsForLabels = (
	containers: Record<string, VerifyContainer>,
	labelsAwaitingResubmit: Set<string>
): Record<string, VerifyContainer> =>
	Object.fromEntries(
		Object.entries(containers).map(([key, container]) =>
			labelsAwaitingResubmit.has(container.label)
				? [key, { ...container, lastSubmittedSignature: null }]
				: [key, container]
		)
	);

const migrateLegacyVerifySubmitQueue = (outboundState: LegacyVerifyState): VerifyState => {
	const {
		activeLabel: _activeLabel,
		lastSyncTimestamp: _lastSyncTimestamp,
		pendingVerifications,
		submitQueue,
		...rehydratedState
	} = outboundState;
	reportDroppedAutomaticVerifications(pendingVerifications);
	const containers = rehydratedState.containers ?? {};
	if (submitQueue === undefined) return { ...rehydratedState, activeContainerId: null, containers };

	const unposted = submitQueue.filter(isUnposted);
	reportStrandedVerifications(unposted);

	return {
		...rehydratedState,
		activeContainerId: null,
		containers: clearSubmitStampsForLabels(containers, new Set(unposted.map((entry) => entry.label)))
	};
};

export default migrateLegacyVerifySubmitQueue;
